import { supabase } from '@/lib/supabase';
import type { Database, RoomRow } from '@/types/database';

type RoomInsert = Database['public']['Tables']['rooms']['Insert'];
type RoomUpdate = Database['public']['Tables']['rooms']['Update'];

export async function listRooms(projectId: string): Promise<RoomRow[]> {
  const { data, error } = await supabase
    .from('rooms')
    .select('*')
    .eq('project_id', projectId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function getRoom(id: string): Promise<RoomRow | null> {
  const { data, error } = await supabase
    .from('rooms')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function createRoom(input: RoomInsert): Promise<RoomRow> {
  const { data, error } = await supabase
    .from('rooms')
    .insert(input)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateRoom(id: string, patch: RoomUpdate): Promise<RoomRow> {
  const { data, error } = await supabase
    .from('rooms')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Mark a room approved, recording who approved it and how (0040).
 *
 * The database requires the name: an approval with nobody's name on it is the
 * blank record that settled no dispute. `approved_via` is set by the trigger
 * from who is calling, so it is deliberately not in the patch — a browser
 * claiming 'portal' would be claiming the client signed.
 */
export async function recordRoomApproval(id: string, approvedBy: string, note: string): Promise<RoomRow> {
  return updateRoom(id, {
    status: 'approved',
    approved_by_name: approvedBy.trim(),
    approval_note: note.trim() || null,
  });
}

export async function deleteRoom(id: string): Promise<void> {
  const { error } = await supabase.from('rooms').delete().eq('id', id);
  if (error) throw error;
}
